import { Injectable } from '@angular/core';
import { IPageData, IPageDataV2 } from '@df/catalog/cms/cms-model.interface';
import { TLoadDependeciesParams } from '@df/catalog/model';
import { ICmsPage } from '@icms/core/resource/page/icms-page';
import { ICmsPageResource } from '@icms/core/resource/page/icms-page.resource';
import uniq from 'lodash-es/uniq';
import { NBlogCategoryPageType } from './blog/page/category-view/cms-blog-page-category-view.component';
import { NBlogPostPageType } from './blog/page/post-view/cms-blog-post.namespace';
import { NBlogRootPageType } from './blog/page/root-view/cms-blog-root-page.namespace';
export class LocalPage<T extends IPageData = IPageData> extends ICmsPage<T> {
    /**
     * Linked posts by tag
     * blog-category type only
     */
    private tagLinkedPosts?: LocalPage<IPageDataV2>[] | undefined;
    /**
     * Extra dependencies
     */

    protected override addDependenciesToLoader(params: TLoadDependeciesParams): void {
        super.addDependenciesToLoader(params);
        const safeParams = { ...params, safe: true };
        if (this.verbosity! > 1) {
            if (this.rawData?.pageType === NBlogPostPageType.typeId) {
                // for blog post load all categories
                params.loader.add(this.loadBlogPostCategories(safeParams));
            }
        }
        if (this.verbosity! > 2) {
            const pageResource = this.resources.page as any as LocalPageResource;
            switch (this.rawData?.pageType) {
                // case NPageStoreFinderPageType.typeId:
                //     promises.push(this.app.resources.store.loadAll());
                //     promises.push(loadMore());
                //     break;
                case NBlogCategoryPageType.typeId:
                    // for blog category load all posts
                    params.loader.add(this.loadBlogCategoryPosts(safeParams));
                    // for blog category load all blog categories categories
                    params.loader.add(pageResource.loadBlogCategories(2, safeParams));
                    // for blog category load root page too
                    params.loader.add(pageResource.loadBlogRoot(2, safeParams));
                    break;
                case NBlogRootPageType.typeId:
                    // for blog category load all posts
                    params.loader.add(pageResource.loadBlogPosts(2, safeParams));
                    // for blog category load all blog categories categories
                    params.loader.add(pageResource.loadBlogCategories(2, safeParams));
                    break;
            }
        }
    }

    /**
     * Loads categories (tags) of a blog post
     * blog-post type only
     */
    private async loadBlogPostCategories(params: TLoadDependeciesParams): Promise<void> {
        const categoryIds = this.getBlogPostCategoryIds();
        if (categoryIds?.length) {
            await this.resources.page.loadAllIds(categoryIds, 2, params);
        }
    }
    /**
     * Loads posts linked to a blog category (including linked by tags entry)
     * blog-category type only
     */
    private async loadBlogCategoryPosts(params: TLoadDependeciesParams): Promise<void> {
        if (this.rawData?.pageType === NBlogCategoryPageType.typeId) {
            this.tagLinkedPosts = (await this.resources.page.find(
                { filter: { 'typeData.tags': this.id }, verbosity: 2 },
                params
            )) as LocalPage<IPageDataV2<any>>[];
            await this.loadChildren(2, params);
        }
    }
    /**
     * Retrieves categories (tags) of a blog post
     * blog-post type only
     */
    getBlogPostCategories(): LocalPage<IPageDataV2>[] | undefined {
        const ids = this.getBlogPostCategoryIds();
        return (
            ids &&
            (this.resources.page
                .getAllExisting(ids)
                .filter(i => i.data?.pageType === NBlogCategoryPageType.typeId) as LocalPage<IPageDataV2>[])
        );
    }
    /**
     * Retrieves categories (tags) ids of a blog post and potentially the root of the blog
     * blog-post type only
     */
    getBlogPostCategoryIds(): number[] | undefined {
        if (this.data?.pageType === NBlogPostPageType.typeId && this.verbosity! > 1) {
            return uniq([this.data.parent, ...((this.data as IPageDataV2).typeData?.tags || [])].filter(i => !!i));
        }
        return undefined;
    }
    /**
     * Retrieves categories (tags) of a blog post
     * blog-post type only
     * @returns
     */
    getBlogCategoryPosts(): LocalPage<IPageDataV2>[] | undefined {
        if (this.verbosity! < 2) {
            return undefined;
        }
        switch (this.data?.pageType) {
            case NBlogCategoryPageType.typeId:
                return (uniq([...(this.children || []), ...(this.tagLinkedPosts || [])]) as LocalPage<IPageDataV2>[]).filter(
                    i => i.data?.pageType === NBlogPostPageType.typeId
                );
            case NBlogRootPageType.typeId:
                return this.resources.page
                    .getAllExisting()
                    .filter(i => i.data?.pageType === NBlogPostPageType.typeId) as LocalPage<IPageDataV2>[];
        }
        return undefined;
    }
}
@Injectable({
    providedIn: 'root'
})
export class LocalPageResource extends ICmsPageResource<LocalPage> {
    static override instance = LocalPage;

    /**
     * Loads all blog categories
     */
    loadBlogRoot(verbosity = 2, params: TLoadDependeciesParams): Promise<LocalPage[]> | void {
        return this.find({ filter: { pageType: NBlogRootPageType.typeId }, verbosity }, params);
    }
    /**
     * Loads all blog categories
     */
    loadBlogCategories(verbosity = 2, params: TLoadDependeciesParams): Promise<LocalPage[]> | void {
        return this.find({ filter: { pageType: NBlogCategoryPageType.typeId }, verbosity }, params);
    }
    /**
     * Loads all blog posts
     */
    loadBlogPosts(verbosity = 2, params: TLoadDependeciesParams): Promise<LocalPage[]> | void {
        return this.find({ filter: { pageType: NBlogPostPageType.typeId }, verbosity }, params);
    }
}
